/* eslint-disable import/no-default-export */
import { HeaderTestIds, NotificationTestIds } from '@trello/test-ids';
import { forTemplate, forNamespace } from '@trello/i18n';
import { PopoverMenu, PopoverMenuButton } from 'app/src/components/PopoverMenu';
import { ChangeNotificationEmailFrequencyPopover } from 'app/src/components/ChangeNotificationEmailFrequencyPopover';
import { InfiniteNotificationsList } from './InfiniteNotificationsList';
// eslint-disable-next-line no-restricted-imports
import {
  setAllNotificationsRead,
  setSeenUnreadNotificationsAction,
} from 'app/gamma/src/modules/state/models/notifications';
// eslint-disable-next-line no-restricted-imports
import { State } from 'app/gamma/src/modules/types';
// eslint-disable-next-line no-restricted-imports
import { updateNotificationEmailFrequency } from 'app/gamma/src/modules/state/models/members';
import React, {
  FunctionComponent,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Transition } from 'react-transition-group';
// eslint-disable-next-line no-restricted-imports
import { getUnreadNotificationsCountGroup } from 'app/gamma/src/selectors/notifications';
// eslint-disable-next-line no-restricted-imports
import {
  NotificationsCountModel,
  NotificationEmailFrequency,
} from 'app/gamma/src/types/models';
import { setNotificationSeenStateGroupCount } from './notificationSeenState';
import { DesktopNotification } from 'app/src/components/DesktopNotification';
import { MarketingOptInBanner } from './MarketingOptInBanner';
import { NotificationOfTosChange } from './NotificationOfTosChange';
import styles from './NotificationsMenu.less';
import { NotificationToggleFilterButton } from './NotificationToggleFilterButton';
import { usePopover, Popover } from '@trello/nachos/popover';
import { Analytics } from '@trello/atlassian-analytics';
import { memberId } from '@trello/session-cookie';
import { idToDate } from '@trello/dates';
import { getMilliseconds } from '@trello/time';
import {
  useNotificationsMenuQuery,
  NotificationsMenuQuery,
} from './NotificationsMenuQuery.generated';

const formatNotificationList = forTemplate('header_notification_list_menu');
const formatTitle = forNamespace('view title');

interface NotificationFrequencyButtonProps {
  notificationEmailFrequency: NotificationEmailFrequency;
  onSetNotificationEmailFrequency: (
    frequency: NotificationEmailFrequency,
  ) => void;
}

const NotificationFrequencyButton: FunctionComponent<NotificationFrequencyButtonProps> = ({
  notificationEmailFrequency,
  onSetNotificationEmailFrequency,
}) => {
  const onShow = useCallback(() => {
    Analytics.sendScreenEvent({
      name: 'notificationsEmailFrequencyInlineDialog',
    });
  }, []);
  const {
    triggerRef,
    toggle,
    popoverProps,
    hide,
  } = usePopover<HTMLButtonElement>({ onShow });
  return (
    <>
      <PopoverMenuButton
        onClick={toggle}
        ref={triggerRef}
        testId={HeaderTestIds.NotificationsEmailFrequency}
      >
        {formatNotificationList('change-notification-email-frequency')}
      </PopoverMenuButton>
      <Popover
        {...popoverProps}
        title={formatTitle('send emails')}
        noHorizontalPadding
      >
        <ChangeNotificationEmailFrequencyPopover
          notificationEmailFrequency={notificationEmailFrequency}
          // eslint-disable-next-line react/jsx-no-bind
          onSelectFrequency={(frequency) => {
            onSetNotificationEmailFrequency(frequency);
            hide();
          }}
        />
      </Popover>
    </>
  );
};

const shouldShowMeMarketingOptIn = (
  member?: NotificationsMenuQuery['member'],
) => {
  if (!member) {
    return false;
  }
  const isMoreThanThreeDaysOld =
    new Date().getTime() - idToDate(member.id).getTime() >
    getMilliseconds({ days: 3 });

  return (
    !!member.confirmed && isMoreThanThreeDaysOld && !member.marketingOptIn?.date
  );
};

const shouldShowMeNoticeOfTosChange = (
  member?: NotificationsMenuQuery['member'],
) => {
  if (!member) {
    return false;
  }

  const hasDismissedMessage = !!(member.messagesDismissed || []).find(
    (message) => message.name === '1-nov-2018-tos-change-accepted',
  );

  return !hasDismissedMessage && new Date(2018, 10, 1) > idToDate(member.id);
};

export const NotificationsMenu: React.FC = () => {
  const dispatch = useDispatch();
  const markAllNotificationsRead = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // This prevents the popover from closing. Because the "Mark All as Read"
      // button gets hidden after being clicked, the popover component thinks
      // that a component from outside the popover was clicked on, which causes
      // it to close.
      e.preventDefault();
      Analytics.sendClickedLinkEvent({
        linkName: 'markAllAsReadLink',
        source: 'notificationsInlineDialog',
      });
      dispatch(setAllNotificationsRead());
    },
    [dispatch],
  );
  const onSetNotificationEmailFrequency = useCallback(
    (frequency: NotificationEmailFrequency) => {
      dispatch(updateNotificationEmailFrequency(frequency));
    },
    [dispatch],
  );
  const setNotificationSeenState = useCallback(
    (unreadCount: NotificationsCountModel) => {
      setNotificationSeenStateGroupCount(unreadCount);
      dispatch(setSeenUnreadNotificationsAction(unreadCount));
    },
    [dispatch],
  );

  const unreadNotificationsCountGroup = useSelector((state: State) =>
    getUnreadNotificationsCountGroup(state),
  );

  const [
    shouldDisplayDesktopNotificationButton,
    setShouldDisplayDesktopNotificationButton,
  ] = useState(DesktopNotification.isUnknown());

  useEffect(() => {
    setNotificationSeenState(unreadNotificationsCountGroup);
  }, [setNotificationSeenState, unreadNotificationsCountGroup]);

  const hasUnreadNotifications =
    Object.values(unreadNotificationsCountGroup).length > 0;

  const requestDesktopNotificationPermissions = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'allowDesktopNotificationsLink',
      source: 'notificationsInlineDialog',
    });
    DesktopNotification.requestPermission().finally(() => {
      setShouldDisplayDesktopNotificationButton(
        DesktopNotification.isUnknown(),
      );
    });
  }, []);

  const { data } = useNotificationsMenuQuery({
    variables: { memberId: memberId || 'me' },
  });
  const shouldShowMarketingOptIn = shouldShowMeMarketingOptIn(data?.member);
  const shouldShowNoticeOfTosChange = shouldShowMeNoticeOfTosChange(
    data?.member,
  );
  const notificationEmailFrequency =
    data?.member?.prefs?.minutesBetweenSummaries ??
    NotificationEmailFrequency.Instantly;

  return (
    <>
      <div className={styles.notificationActions}>
        <NotificationToggleFilterButton />
        {!!hasUnreadNotifications && (
          <button
            className={styles.notificationAction}
            onClick={markAllNotificationsRead}
            data-test-id={NotificationTestIds.MarkAllReadButton}
          >
            {formatNotificationList('mark-all-as-read')}
          </button>
        )}
      </div>
      <div className={styles.notificationsPanel}>
        <Transition in={shouldShowMarketingOptIn} timeout={2000}>
          {(state) => state !== 'exited' && <MarketingOptInBanner />}
        </Transition>
        {shouldShowNoticeOfTosChange && <NotificationOfTosChange />}
        <InfiniteNotificationsList />
        <PopoverMenu>
          <NotificationFrequencyButton
            notificationEmailFrequency={notificationEmailFrequency}
            onSetNotificationEmailFrequency={onSetNotificationEmailFrequency}
          />
          {shouldDisplayDesktopNotificationButton && (
            <PopoverMenuButton onClick={requestDesktopNotificationPermissions}>
              {formatNotificationList('allow-desktop-notifications')}
            </PopoverMenuButton>
          )}
        </PopoverMenu>
      </div>
    </>
  );
};
